/**
 * @file    useProgramsManager.ts
 * @scope   VIEW
 * @summary Orchestration hook for the CPR ProgramsManager view: program CRUD/reorder, rollup-level
 *          calculation settings, expand/touched UI state and derived validation flags.
 * @consumes useCprContext, RollupMetricModel, programsManagerModel, ProgramCatalog
 * @produces State, handlers and derived data the ProgramsManager component renders
 * @sideEffects Dispatches `UPDATE_ROLLUP_METRIC` actions to the CPR context.
 * @notes The rollup lives in `CprContext` (single source of truth); only UI state is local.
 *        Programs/questions/filter fields come from the injected `ProgramCatalog`.
 */
import { useCallback, useMemo, useState } from 'react';
import { useCprContext } from '../../state/CprContext';
import {
  RollupMetricModel,
  RollupProgramModel,
  createRollupProgram,
} from 'models/CrossProgramRollup/RollupMetricModel';
import { FieldFilter } from 'models/Enterprise/FieldsManagerModel';
import { hasIncompleteFilters } from '../../../Enterprise/utils/validation/filterValidation';
import { ProgramCatalog } from './useProgramCatalog';
import {
  getFilterCountText,
  isProgramInvalid,
  isProgramSelectionIncomplete,
  toRollupCalculations,
} from './programsManagerModel';
import { CprAggregationType } from '../../model';

export const useProgramsManager = (
  rollupId: string | null,
  catalog: ProgramCatalog,
  expandedProgramId: string | null = null
) => {
  const { state, dispatch } = useCprContext();
  const metric: RollupMetricModel | undefined = state.metrics.find((m) => m.id === rollupId);
  const programs = useMemo(() => metric?.programs ?? [], [metric]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    if (expandedProgramId) return new Set([expandedProgramId]);
    return new Set(programs.length === 1 ? [programs[0].id] : []);
  });
  const [touchedIds, setTouchedIds] = useState<Set<string>>(new Set());

  const updateMetric = useCallback(
    (changes: Partial<Omit<RollupMetricModel, 'id'>>) => {
      if (rollupId) dispatch({ type: 'UPDATE_ROLLUP_METRIC', id: rollupId, changes });
    },
    [dispatch, rollupId]
  );

  const updateProgram = useCallback(
    (id: string, changes: Partial<RollupProgramModel>) =>
      updateMetric({ programs: programs.map((p) => (p.id === id ? { ...p, ...changes } : p)) }),
    [programs, updateMetric]
  );

  /** WHY: `calculations` is what the serializer persists as `CustomCalculations`. */
  const handleCalculationTypeChange = useCallback(
    (option: { value: string } | null) =>
      updateMetric({
        calculationType: option?.value,
        calculations: toRollupCalculations(option?.value),
      }),
    [updateMetric]
  );

  /** Widget-level `AggregationType` (no default on purpose: docs/guardrails.md). */
  const handleAggregationTypeChange = useCallback(
    (option: { value: CprAggregationType } | null) =>
      dispatch({ type: 'SET_AGGREGATION_TYPE', aggregationType: option?.value }),
    [dispatch]
  );

  const handlePrecisionChange = useCallback(
    (option: { value: number } | null) => updateMetric({ precision: option?.value }),
    [updateMetric]
  );

  /** Appends a blank program, expanded, and validates the existing ones. */
  const handleAddProgram = useCallback(() => {
    const program = createRollupProgram();
    setTouchedIds((prev) => new Set([...Array.from(prev), ...programs.map((p) => p.id)]));
    setExpandedIds((prev) => new Set([...Array.from(prev), program.id]));
    updateMetric({ programs: [...programs, program] });
  }, [programs, updateMetric]);

  const handleRemoveProgram = useCallback(
    (id: string) => updateMetric({ programs: programs.filter((p) => p.id !== id) }),
    [programs, updateMetric]
  );

  /** Changing the program invalidates the question (each program has its own questions). */
  const handleProgramSelect = useCallback(
    (id: string, option: { value: string } | null) => {
      const selected = catalog.programOptions.find((p) => p.value === option?.value);
      updateProgram(id, {
        dataCollectionGuid: selected?.value ?? '',
        originId: selected?.originId ?? '',
        displayName: selected?.label,
        questionGuid: undefined,
        questionLabel: undefined,
      });
    },
    [catalog.programOptions, updateProgram]
  );

  const handleQuestionSelect = useCallback(
    (id: string, option: { value: string; label: string } | null) =>
      updateProgram(id, { questionGuid: option?.value, questionLabel: option?.label }),
    [updateProgram]
  );

  const handleReorderPrograms = useCallback(
    (draggedIndex: number, targetIndex: number) => {
      if (
        Number.isNaN(draggedIndex) ||
        draggedIndex === targetIndex ||
        draggedIndex < 0 ||
        targetIndex < 0 ||
        draggedIndex >= programs.length ||
        targetIndex >= programs.length
      ) {
        return;
      }
      const next = [...programs];
      const [moved] = next.splice(draggedIndex, 1);
      next.splice(targetIndex, 0, moved);
      updateMetric({ programs: next });
    },
    [programs, updateMetric]
  );

  const handleFilterRemove = useCallback(
    (id: string, filterId: string) => {
      const program = programs.find((p) => p.id === id);
      if (program) {
        updateProgram(id, { filters: (program.filters ?? []).filter((f) => f.id !== filterId) });
      }
    },
    [programs, updateProgram]
  );

  const handleAccordionToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(
    () => setExpandedIds(new Set(programs.map((p) => p.id))),
    [programs]
  );
  const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

  /** A card is touched once focus leaves it (react-select menus live in a portal: ignored). */
  const handleProgramBlur = useCallback((e: React.FocusEvent<HTMLDivElement>, id: string) => {
    const next = e.relatedTarget;
    if (next instanceof HTMLElement) {
      if (next.closest('[class*="-menu"]') || next.id?.includes('react-select')) return;
      if (e.currentTarget.contains(next)) return;
    }
    setTouchedIds((prev) => (prev.has(id) ? prev : new Set([...Array.from(prev), id])));
  }, []);

  const partitionFilters: FieldFilter[] = metric?.partitionFilters ?? [];

  const hasInvalidPrograms = programs.some((p) => isProgramInvalid(p, touchedIds.has(p.id)));
  const hasInvalidPartitionFilters = hasIncompleteFilters(partitionFilters);

  /** Untouched-agnostic validity: gates the Apply button. */
  const canApply =
    !!metric?.calculationType &&
    !!state.aggregationType &&
    programs.length > 0 &&
    !programs.some(
      (p) => isProgramSelectionIncomplete(p) || hasIncompleteFilters(p.filters ?? [])
    ) &&
    !hasInvalidPartitionFilters;

  return {
    metric,
    aggregationType: state.aggregationType,
    canApply,
    programs,
    programOptions: catalog.programOptions,
    getQuestionOptions: catalog.getQuestionOptions,
    getFilterFields: catalog.getFilterFields,
    expandedIds,
    touchedIds,
    areAllExpanded: programs.length > 0 && expandedIds.size === programs.length,
    filterCountText: getFilterCountText(partitionFilters),
    hasInvalidPrograms,
    hasInvalidPartitionFilters,
    handleCalculationTypeChange,
    handleAggregationTypeChange,
    handlePrecisionChange,
    handleAddProgram,
    handleRemoveProgram,
    handleProgramSelect,
    handleQuestionSelect,
    handleReorderPrograms,
    handleFilterRemove,
    handleAccordionToggle,
    handleProgramBlur,
    expandAll,
    collapseAll,
  };
};
